import React, { useState, useEffect, lazy, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { hasRestrictedRole, hasValue, isValidObject, getCompanyTimeZone } from 'globalutils/fmUtils';
import { ROLES } from 'globalconfig/constants/keys';
import { udListColumns } from './config';
import { renderRow } from './UdAssignTableRow';
import { checkServiceProvider } from './serviceProviderConfig';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Form = lazy(() => import(`globalcomponents/UI/Form/${layout}`));
const Table = lazy(() => import(`globalcomponents/UI/Table/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`);

const UnassignedEntry = ({
  unAssignedEntries,
  id,
  saveUnAssignedEntries,
  setUdAssignEntryList,
  callback,
  apiStatus,
  Styled,
  selectedDriverProfile,
  saveIconClicked,
  setSaveIconClicked,
  selectedId,
  setSelectedId,
  errorMsg,
  setError,
  logDate,
}) => {
  useEffect(() => {
    if (saveIconClicked && selectedId === id && document.getElementsByClassName('udAssignSubmit' + selectedId)) {
      document.getElementsByClassName('udAssignSubmit' + selectedId)[0].click();
      setSaveIconClicked(false);
    }
  }, [saveIconClicked]);

  const viewOnly = hasRestrictedRole([ROLES.VIEW_ONLY_FM]);

  const unAssignedEntryForm = () => {
    const apiId = 'unAssignedEntryForm';
    const hasUpdateunAssignedEntryApiStatus = hasValue(apiStatus.status, 'api', apiId);
    return (
      <>
        <Form
          type="server"
          direction="row"
          initialValues={{ time_from: '' }}
          RenderComponent={RenderComponent}
          handleOnSubmit={(fields) => {
            saveUnAssignedEntries({
              apiId,
              data: { id: id, tab: selectedDriverProfile },
              callback: callback,
            });
          }}
          customProps={{
            hasUpdateunAssignedEntryApiStatus,
            setUdAssignEntryList,
            unAssignedEntries,
            id,
          }}
        />
      </>
    );
  };

  const RenderComponent = ({ handleEdit, validateForm, customProps, submitAction }) => {
    const SERVICE_PROVIDER = window.$environment.SERVICE_PROVIDER;
    const [list, setList] = useState([]);
    const [companyCurrentDate, setCompanyCurrentDate] = useState('');
    const [drivers, setDrivers] = useState([]);
    const [validationMessage, setValidationMessage] = useState('');
    const { i18n } = useTranslation();
    const [isValid, setIsValid] = useState(true);
    const { id, unAssignedEntries } = customProps;

    const convertTZ = () => {
      const date = new Date();
      const companyTimeZone = getCompanyTimeZone();
      const companyDate = companyTimeZone
        ? date.toLocaleString('en-US', { timeZone: companyTimeZone }).split(',')[0]
        : date.toLocaleString('en-US', {}).split(',')[0];
      setCompanyCurrentDate(companyDate);
      if (new Date(companyDate).getTime() === new Date(logDate).getTime()) {
        setValidationMessage(i18n.t('Assignment of the logs to the current day is restricted!'));
        setIsValid(false);
      }
    };

    useEffect(() => {
      const data = unAssignedEntries[id] || {};
      const driversList = Object.keys(data.companyDrivers || {}).map((key) => {
        const item = data.companyDrivers[key];
        return {
          label: item,
          value: key,
        };
      });
      const filteredList = filteredListUdLog();
      const finalList = filteredList.filter((item) => isValidObject(item));
      setList(finalList || []);
      convertTZ();
      setDrivers(driversList);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unAssignedEntries]);

    useEffect(() => {
      const container = document.getElementById('tdEntry' + id);
      const source = document.getElementById('entry' + id);

      const calHt = checkServiceProvider('udAssignEntryList_calHt', SERVICE_PROVIDER, false);
      const calHtMul = checkServiceProvider('udAssignEntryList_calHtMul', SERVICE_PROVIDER, false);

      if (container && list?.filter((item) => item.status === 'DRIVING').length) {
        container.height = (calHtMul * list?.filter((item) => item.status === 'DRIVING').length + calHt).toString();
        source.style.display = 'block';
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list]);

    const drivingList = useMemo(() => {
      return list?.filter((item) => item.status === 'DRIVING') || [];
    }, [list]);

    const updateField = (payload) => {
      let source = [...list];
      source = source.map((item) => {
        if (
          item.id === payload.id ||
          (payload.needFullUpdate && !item[payload.field]?.label && ['driverId', 'coDriverId'].includes(payload.field))
        ) {
          item[payload.field] = payload.value;
        }
        if (payload.data && !item[payload.field] && ['location', 'note'].includes(payload.field)) {
          item[payload.field] = payload.data;
        }
        if (payload.data && !item[payload.field]?.value && ['driverId', 'coDriverId'].includes(payload.field))
          item[payload.field] = payload.data;

        return item;
      });
      setList(source);
    };

    const filteredListUdLog = () => {
      const data = unAssignedEntries[id] || {};
      return Object.keys(data.dailyLogEntries || {}).map((key) => {
        const item = data.dailyLogEntries[key];

        return {
          ...item,
          driverId: item?.driverId?.value
            ? item.driverId
            : item?.driverId === 0 || item?.driverId?.value === 0
              ? { value: 0, label: 'Please Select' }
              : {
                value: item.driverId,
                label: data.companyDrivers[item.driverId],
              },
          coDriverId:
            item?.coDriverId?.value || item.coDriverId
              ? {
                label: unAssignedEntries[id].companyDrivers[item?.coDriverId?.value || item.coDriverId],
                value: item?.coDriverId?.value || item.coDriverId,
              }
              : '',
          key: item.id,
        };
      });
    };

    const generateRows = (handleEdit) => {
      const generatedUi = drivingList.map((item) => {
        return renderRow({
          item,
          handleEdit,
          id,
          updateField,
          i18n,
          drivers,
          viewOnly,
        });
      });
      return generatedUi;
    };

    const handleValidate = (drivingData, validData) => {
      const validateResponse = validateForm();
      validateResponse.then((data) => {
        let validity = true;
        // Mandatory driver validation for all DRIVING entries (including when UD bulk edit is disabled)
        for (let i = 0; i < drivingData.length; i++) {
          const item = drivingData[i];
          if (!item.driverId?.value || item.driverId.value === 0) {
            validity = false;
            setValidationMessage(i18n.t('Please fill in atleast one assigned driver'));
            if (window.$environment.CURRENT_LAYOUT === 'SpireonNew') {
              setError(i18n.t('Please fill in atleast one assigned driver'));
            }
            break;
          }
        }
        // Notes and co-driver validation only for editable entries
        if (validity && validData.length) {
          for (let i = 0; i < validData.length; i++) {
            const item = validData[i];
            if (item.note?.length < 4 && !item.udReadOnly && item.status === 'DRIVING') {
              validity = false;
              setValidationMessage('Please enter atleast 4 characters in notes');
              if (window.$environment.CURRENT_LAYOUT === 'SpireonNew') setError('Please enter atleast 4 characters in notes');
              break;
            }
            if (item.coDriverId && item.driverId && item.driverId.value === item.coDriverId.value) {
              validity = false;
              setValidationMessage('Driver and Co-Driver cannot be same, Please fill in different values');
              if (window.$environment.CURRENT_LAYOUT === 'SpireonNew')
                setError('Driver and Co-Driver cannot be same, Please fill in different values');
              break;
            }
          }
        }
        setIsValid(validity);
        if (validity && validData.length) {
          console.log({ list, id });
          setUdAssignEntryList({ list, id });
          submitAction(data);
          setSelectedId(null);
        }
      });
    };

    const handleSave = async () => {
      const spListData = selectedId && unAssignedEntries[selectedId];
      const listData = window.$environment.CURRENT_LAYOUT === 'SpireonNew' ? (spListData ? filteredListUdLog() : list) : list;

      const drivingData = listData.filter((ud) => ud.status === 'DRIVING');
      const validData = listData.filter((ud) => ud.udReadOnly === false && ud.status === 'DRIVING');

      if (!drivingData.length) {
        callback('error', i18n.t('No valid entries to save'));
        return;
      }

      handleValidate(drivingData, validData);
    };

    return (
      <Styled.UnidentifiedSection>
        <div className="floatingudTable" style={{}}>
          <Styled.UdLists>
            <Table
              type="client"
              title=""
              columns={udListColumns}
              data={generateRows(handleEdit)}
              emptyMessage={''}
              itemsPerPage={15}
              actions={[]}
              innerTable
              i18n={i18n}
              noPagination
            />
          </Styled.UdLists>
          <div style={{ display: 'flex', justifyContent: 'end' }} className="footer">
            {window.$environment.CURRENT_LAYOUT === 'SpireonNew'
              ? errorMsg && selectedId === id && <Message type="error" message={errorMsg} />
              : !isValid && <Message type="error" message={validationMessage} />}
            {!viewOnly && (
              <FormStyle.default.ButtonsWrapper className="saveButton">
                <Button
                  type="submit"
                  label={i18n.t('common.save')}
                  disabled={new Date(companyCurrentDate).getTime() === new Date(logDate).getTime()}
                  className={'udAssignSubmit' + id}
                  onClick={handleSave}
                />
              </FormStyle.default.ButtonsWrapper>
            )}
          </div>
        </div>
      </Styled.UnidentifiedSection>
    );
  };

  return unAssignedEntryForm();
};

export default UnassignedEntry;
